import { InsertLinkTwoTone } from '@mui/icons-material';
import { Chip, Link, SxProps, Theme } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import tinycolor from 'tinycolor2';
import { emitAnalyticsEvent, emitAnalyticsEventAndWait, shouldRecordClick } from '@/lib/analyticsClient';
import { safeGetItem, safeSetItem } from '@/utils/safeStorage';
import { isOpenArtistSlug, toArtistSlug } from '@/utils/artistSlug';

export const siteId = typeof window !== 'undefined' ? `${import.meta.env.VITE_SITE_ID || ''}` : '';

export const darkerColor = (color1: string, color2: string): string => {
  color1 = color1.includes('var') ? generateColor(color1)?.bgColor || color1 : color1;
  color2 = color2.includes('var') ? generateColor(color2)?.bgColor || color2 : color2;

  if (tinycolor(color1).getBrightness() < tinycolor(color2).getBrightness()) {
    return color1;
  } else {
    return color2;
  }
};

export default function fbTrack(a: string, b?: Record<string, unknown>): void {
  if (import.meta.env.PROD && typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq(a, b);
  }
}

export type Platform =
  | 'spotify'
  | 'apple'
  | 'youtube'
  | 'itunes'
  | 'soundcloud'
  | 'tidal'
  | 'amazon';

export interface TrackingEventData {
  event: string;
  platform?: Platform;
  artist?: string;
  track?: string;
  album?: string;
  kind?: string;
  id?: string;
  destinationUrl?: string;
  sourcePath?: string;
  section?: string;
  action?: string;
  value?: string | number;
  target?: string;
  label?: string;
  href?: string;
  trackId?: string;
  releaseKey?: string;
  releaseName?: string;
  companionPlaylist?: boolean;
  query?: string;
  referrer?: string;
  designKey?: string;
  sku?: string;
  color?: string;
  size?: string;
  orderReference?: string;
  items?: number;
  country?: string;
  garment?: string;
  art?: string;
  sort?: string;
  discountCode?: string;
  discountCents?: number;
}

export function oncePerSession(key: string): boolean {
  if (typeof window === 'undefined') return false;
  if (safeGetItem(key, 'session')) return false;
  safeSetItem(key, String(Date.now()), 'session');
  return true;
}

const DEFAULT_GA_MEASUREMENT_ID = 'G-Z1TLN1YPND';
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || DEFAULT_GA_MEASUREMENT_ID;
let hasInitializedGoogleAnalytics = false;

export function initializeGoogleAnalytics(): void {
  if (typeof window === 'undefined' || hasInitializedGoogleAnalytics || !GA_MEASUREMENT_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...args: unknown[]) => {
    window.dataLayer?.push(args);
  });

  if (!document.querySelector(`script[data-ga-measurement-id="${GA_MEASUREMENT_ID}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.dataset.gaMeasurementId = GA_MEASUREMENT_ID;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
  hasInitializedGoogleAnalytics = true;
}

export function trackGoogleEvent(eventName: string, data?: TrackingEventData): void {
  if (typeof window === 'undefined') return;
  initializeGoogleAnalytics();
  window.gtag?.('event', eventName, data || {});
}

const INTERNAL_EVENT_NAMES: Record<string, string> = {
  PlatformClick: 'platform_click',
  PlatformRedirect: 'platform_redirect',
  OutboundRedirect: 'outbound_redirect',
  SectionView: 'section_view',
  UserInteraction: 'interaction',
  Click: 'click',
};

const INTERNAL_EVENT_CATEGORIES: Record<string, string> = {
  PlatformClick: 'platform',
  PlatformRedirect: 'platform',
  OutboundRedirect: 'platform',
  SectionView: 'engagement',
  UserInteraction: 'engagement',
  Click: 'click',
};

function analyticsInputFromTracking(eventName: string, data?: TrackingEventData) {
  return {
    eventName: INTERNAL_EVENT_NAMES[eventName] || eventName,
    eventCategory: INTERNAL_EVENT_CATEGORIES[eventName] || 'user_action',
    artist: data?.artist,
    platform: data?.platform,
    trackId: data?.trackId,
    trackName: data?.track,
    releaseKey: data?.releaseKey,
    releaseName: data?.releaseName || data?.track || data?.album,
    referrer: data?.referrer,
    metadata: {
      event: data?.event,
      kind: data?.kind,
      id: data?.id,
      destinationUrl: data?.destinationUrl,
      sourcePath: data?.sourcePath,
      section: data?.section,
      action: data?.action,
      value: data?.value,
      target: data?.target,
      label: data?.label,
      href: data?.href,
      query: data?.query,
      referrer: data?.referrer,
      designKey: data?.designKey,
      sku: data?.sku,
      color: data?.color,
      size: data?.size,
      orderReference: data?.orderReference,
      items: data?.items,
      country: data?.country,
      garment: data?.garment,
      art: data?.art,
      sort: data?.sort,
    },
  };
}

export function trackEvent(eventName: string, data?: TrackingEventData): void {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('trackCustom', eventName, data);
  }
  trackGoogleEvent(eventName, data);
  emitAnalyticsEvent(analyticsInputFromTracking(eventName, data));
}

export function trackEventAndWait(eventName: string, data?: TrackingEventData, timeoutMs?: number): Promise<string | null> {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('trackCustom', eventName, data);
  }
  trackGoogleEvent(eventName, data);
  return emitAnalyticsEventAndWait(analyticsInputFromTracking(eventName, data), timeoutMs);
}

export interface ClickTrackingOptions {
  label?: string;
  artist?: string;
  track?: string;
  trackId?: string;
  album?: string;
  releaseKey?: string;
  releaseName?: string;
  platform?: Platform;
  section?: string;
  href?: string;
  value?: string | number;
}

export function trackClick(target: string, options?: ClickTrackingOptions): void {
  if (!target || !shouldRecordClick(target)) return;
  trackEvent('Click', {
    event: `click-${target}`,
    target,
    label: options?.label || target,
    artist: options?.artist,
    track: options?.track,
    trackId: options?.trackId,
    album: options?.album,
    releaseKey: options?.releaseKey,
    releaseName: options?.releaseName,
    platform: options?.platform,
    section: options?.section,
    href: options?.href,
    action: target,
    value: options?.value,
  });
}

export function trackPlatformClick(
  platform: Platform,
  artist?: string,
  track?: string,
  release?: { releaseKey?: string; releaseName?: string; companionPlaylist?: boolean }
): void {
  trackEvent('PlatformClick', {
    event: `${platform}-click`,
    platform,
    artist,
    track,
    releaseKey: release?.releaseKey,
    releaseName: release?.releaseName,
    companionPlaylist: release?.companionPlaylist,
    target: `platform:${platform}`,
    label: `${platform} link`,
  });
}

export function trackRedirect(
  platform: Platform,
  artist?: string,
  track?: string,
  release?: { releaseKey?: string; releaseName?: string; companionPlaylist?: boolean }
): Promise<string | null> {
  return trackEventAndWait('PlatformRedirect', {
    event: `${platform}-redirect`,
    platform,
    artist,
    track,
    releaseKey: release?.releaseKey,
    releaseName: release?.releaseName,
    companionPlaylist: release?.companionPlaylist,
    target: `platform-redirect:${platform}`,
    label: `${platform} auto redirect`,
  });
}

export function trackOutboundRedirect(data: {
  platform: Platform | string;
  kind: string;
  id: string;
  destinationUrl: string;
  sourcePath?: string;
  query?: string;
  referrer?: string;
}): Promise<string | null> {
  return trackEventAndWait('OutboundRedirect', {
    event: `${data.platform}-${data.kind}-redirect`,
    platform: data.platform as Platform,
    kind: data.kind,
    id: data.id,
    destinationUrl: data.destinationUrl,
    sourcePath: data.sourcePath,
    query: data.query,
    referrer: data.referrer,
    target: `shortlink:${data.platform}:${data.kind}`,
    label: `short link ${data.platform} ${data.kind}`,
    href: data.destinationUrl,
  });
}

export function trackSectionView(section: string, artist?: string): void {
  trackEvent('SectionView', {
    event: `section-view-${section}`,
    section,
    artist,
  });
}

export function trackInteraction(action: string, value?: string | number): void {
  trackEvent('UserInteraction', {
    event: action,
    action,
    value,
  });
}

export interface URLParams {
  spotifyOnly?: boolean;
  youtubeOnly?: boolean;
  itunesOnly?: boolean;
  appleMusicOnly?: boolean;
  redirect?: Platform;
  adMode?: boolean;
}

export function parseURLParams(): URLParams {
  if (typeof window === 'undefined') return {};

  return parseURLParamsFrom(window.location.search);
}

const FALSE_FLAG_VALUES = new Set(['false', '0', 'no', 'off']);

export function parseURLParamsFrom(search: string): URLParams {
  const params = new URLSearchParams(search);
  const flag = (name: string) => params.has(name) && !FALSE_FLAG_VALUES.has((params.get(name) || '').trim().toLowerCase());
  const only = (params.get('only') || '').trim().toLowerCase();
  const redirect = (params.get('redirect') || '').trim().toLowerCase();

  return {
    spotifyOnly: flag('spotifyOnly') || only === 'spotify',
    youtubeOnly: flag('youtubeOnly') || only === 'youtube',
    itunesOnly: flag('itunesOnly') || only === 'itunes',
    appleMusicOnly: flag('appleMusicOnly') || only === 'apple' || only === 'applemusic',
    redirect: redirect ? (redirect as Platform) : undefined,
    adMode: flag('adMode') || flag('ad'),
  };
}

export function showsReleaseVideo(params: URLParams): boolean {
  return !(params.spotifyOnly || params.itunesOnly || params.appleMusicOnly);
}

export function getPlatformURL(
  platform: Platform,
  urls: {
    spotifyURL?: string;
    appleMusicURL?: string;
    youtubeURL?: string;
    itunesURL?: string;
    soundcloudURL?: string;
    tidalURL?: string;
    amazonURL?: string;
  }
): string | undefined {
  switch (platform) {
    case 'spotify':
      return urls.spotifyURL;
    case 'apple':
    case 'itunes':
      return urls.appleMusicURL || urls.itunesURL;
    case 'youtube':
      return urls.youtubeURL;
    case 'soundcloud':
      return urls.soundcloudURL;
    case 'tidal':
      return urls.tidalURL;
    case 'amazon':
      return urls.amazonURL;
    default:
      return undefined;
  }
}

export function handlePlatformRedirect(
  platform: Platform,
  urls: {
    spotifyURL?: string;
    appleMusicURL?: string;
    youtubeURL?: string;
    itunesURL?: string;
  },
  artist?: string,
  track?: string,
  release?: { releaseKey?: string; releaseName?: string; companionPlaylist?: boolean }
): void {
  const url = getPlatformURL(platform, urls);
  if (url) {
    void trackRedirect(platform, artist, track, release).finally(() => {
      window.location.href = url;
    });
  }
}

export const BREAKPOINT = 900;

export function TypeOfWindowUndefined(): boolean {
  return typeof window === 'undefined';
}

export function TypeOfWindowDefined(): boolean {
  return typeof window !== 'undefined';
}

export const mapAlternateColors = (colors: string[]): Record<string, string> | undefined => {
  if (TypeOfWindowDefined()) {
    const altColorVars: Record<string, string> = {};
    colors.forEach((color, index) => {
      altColorVars[`--color${index + 1}`] = color;
    });
    return altColorVars;
  }
};

export const hexToRgb = (hex = '#ffffff'): string | undefined => {
  if (TypeOfWindowDefined()) {
    const rgb = tinycolor(hex).toRgbString();
    return rgb;
  }
};

export const rgbToHex = (rgb = 'rgb(0,0,0)'): string | undefined => {
  if (TypeOfWindowDefined()) {
    const hex = tinycolor(rgb).toHexString();
    return hex;
  }
};

export function rgbtoHsv(rgb = 'rgb(0,0,0)'): [number, number, number] | undefined {
  if (TypeOfWindowDefined()) {
    const color = tinycolor(rgb);
    const hsv = (color as unknown as { toHsv: () => { h: number; s: number; v: number } }).toHsv();
    const { h, s, v } = hsv;
    return [h, s, v];
  }
}

export function sortColors(a: string, b: string): number {
  if (TypeOfWindowDefined()) {
    const a1 = rgbtoHsv(a);
    const b1 = rgbtoHsv(b);
    if (a1 && b1) {
      return b1[1] + b1[2] - (a1[1] + a1[2]);
    }
  }
  return 0;
}

export const getTextColor = (bgColor: string): string | undefined => {
  if (TypeOfWindowDefined()) {
    const parsed = tinycolor(bgColor);
    if (!(parsed as any).isValid?.()) return '#ffffff';

    return tinycolor.readability(parsed, '#000000') >= tinycolor.readability(parsed, '#ffffff')
      ? '#000000'
      : '#ffffff';
  }
};

export const removeHyphens = (str: string | undefined): string | undefined => {
  if (!str) return;
  return str.replace(/-|_/g, ' ');
};

export const lc = (str: string | undefined): string | undefined => {
  if (!str) return;
  return String(str).toLowerCase();
};

export const uc = (str: string | undefined): string | undefined => {
  if (!str) return;
  return String(str).toUpperCase();
};

export const cap = (str: string | undefined): string | undefined => {
  if (!str) return;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const safeString = (str: string | undefined): string | undefined => {
  if (!str) return;
  return lc(str.replace(/[^a-z0-9]/gi, ''));
};

export const safeStringWithHyphens = (str: string | undefined): string | undefined => {
  if (!str) return;
  return lc(str.replace(/[^a-z0-9 ]/gi, '').replace(/ /g, '-'));
};

export const chipStyle: SxProps<Theme> = {
  backgroundColor: 'rgba(1, 1, 1, 0.07)',
  borderRadius: '4px',
  color: 'var(--textColor)',
  overflow: 'hidden',
  fontSize: { xs: '13px', md: '12px' },
  lineHeight: 1.4,
  minHeight: { xs: '26px', md: '24px' },
  fontFamily: 'Poppins, sans-serif',
  '&:hover': {
    backgroundColor: 'rgba(1, 1, 1, 0.14)',
    color: 'var(--textColor)',
  },
  '.MuiChip-icon': {
    color: 'var(--textColor)',
    fontSize: { xs: '13px', md: '12px' },
    lineHeight: 1.4,
  },
  '.MuiChip-label': { lineHeight: '1', overflow: 'initial' },
};

export const chipStyleHeader: SxProps<Theme> = {
  ...(chipStyle as object),
  backgroundColor: 'transparent',
  border: '1px solid',
  borderColor: 'var(--sidebarHeaderColor)',
  textTransform: 'uppercase',
  fontFamily: 'Poppins,sans-serif',
  fontSize: { xs: '1.75rem', sm: '1rem', md: '0.875rem', lg: '0.75rem' },
  lineHeight: { xs: '2.2', md: '1.6' },
  letterSpacing: '1.1px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'var(--sidebarHeaderColor)',
  },
  '.MuiChip-icon': {
    color: 'var(--textColor)',
    fontSize: { xs: '1.75rem', sm: '1rem', md: '0.875rem', lg: '0.75rem' },
    lineHeight: { xs: '2.2', md: '1.6' },
  },
  '.MuiChip-label': { color: 'var(--sidebarHeaderColor)', height: '100%' },
};

export const chipStyleSummary: SxProps<Theme> = {
  ...(chipStyle as object),
  height: 'unset',
  padding: '8px',
  '.MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'break-spaces',
  },
};

export const chipStyleLink = (color = 'var(--color3)', noHover = false): SxProps<Theme> => {
  return {
    ...(chipStyle as object),
    backgroundColor: color,
    minHeight: { xs: '26px', md: '24px' },
    gap: { xs: '0.45rem', md: '0.15rem' },
    '.MuiChip-label': {
      color: generateColor(color)?.textColor,
      overflow: 'initial',
      paddingLeft: { xs: '0.4rem', md: '12px' },
      paddingRight: { xs: '0.75rem', md: '12px' },
    },
    '.MuiChip-icon': {
      color: generateColor(color)?.textColor,
      fontSize: { xs: '13px', md: '12px' },
      lineHeight: 1.4,
      marginLeft: { xs: '0.65rem', md: '5px' },
      marginRight: { xs: '0.1rem', md: '-6px' },
    },
    '&:hover': !noHover
      ? {
          backgroundColor: tinycolor(generateColor(color)?.bgColor || 'var(--logoColor2)')
            .darken(18)
            .toString(),
          color: generateColor(color)?.textColor || 'var(--logoColor)',
          '.MuiChip-label': {
            backgroundColor: 'transparent',
            color: generateColor(color)?.textColor || 'inherit',
          },
        }
      : { backgroundColor: 'color' },
  };
};

interface ColorResult {
  textColor: string | undefined;
  bgColor: string;
}

export const generateColor = (colorVariable: string): ColorResult | undefined => {
  if (TypeOfWindowDefined() && document.querySelector('.main') !== null) {
    colorVariable = colorVariable.replace('var(', '').replace(')', '');
    const mainElement = document.querySelector('.main');
    if (!mainElement) return;
    const color3 = getComputedStyle(mainElement).getPropertyValue(colorVariable);

    const bgColor = color3;
    const textColor = getTextColor(bgColor);

    return { textColor, bgColor };
  }
};

export const formatDate = (date: string | Date): string => {
  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return formattedDate;
};

interface Album {
  album_group: string;
  release_date: string;
  total_tracks: number;
  artists?: Array<{ name: string }>;
  name?: string;
}

interface AlbumData {
  albums: Album[];
}

interface TopTrack {
  name: string;
  artists: Array<{ name: string }>;
}

interface ArtistData {
  name: string;
  images?: Array<{ url: string }>;
  followers?: { total: number };
  genres?: string[];
  topTracks?: TopTrack[];
}

interface Song {
  artist?: string;
  artistUsername?: string;
}

let maxRetries = 4;
export const generateSummary = (
  albumData: AlbumData | null | undefined,
  artistData: ArtistData | null | undefined,
  song: Song | null | undefined,
  isArtistPage = false,
  color = 'var(--color7)',
  showHalfBio?: boolean
): React.ReactNode => {
  console.log('generateSummary', albumData, artistData, song, isArtistPage, color, showHalfBio);
  if (!albumData || !artistData) {
    if (maxRetries > 0) {
      setTimeout(() => {
        generateSummary(albumData, artistData, song, isArtistPage, color, showHalfBio);
      }, 1000);
      maxRetries--;
    }
    return null;
  }

  const allAlbums = albumData.albums.filter((album) => {
    return album.album_group !== 'appears_on';
  });
  const singles = allAlbums.filter((album) => {
    return album.album_group === 'single';
  });
  const { name, followers, genres, topTracks } = artistData || {};

  const albums = allAlbums.filter((album) => {
    return (
      album.album_group !== 'appears_on' &&
      (album.album_group === 'album' ||
        album.album_group === 'compilation' ||
        album.total_tracks > 2)
    );
  });

  if (!name) {
    if (maxRetries > 0) {
      setTimeout(() => {
        generateSummary(albumData, artistData, song, isArtistPage, color, showHalfBio);
      }, 1000);
      maxRetries--;
    }
    return null;
  }

  const numAlbums = albums?.length || 0;
  const sortedAlbums = [...allAlbums].sort((a, b) => {
    return new Date(a.release_date).getTime() - new Date(b.release_date).getTime();
  });
  const startYear = sortedAlbums[0]?.release_date.split('-')[0];
  const endYear = new Date().getFullYear();
  const yearsActive = endYear - Number(startYear);

  const collaborators = new Set<string>();
  albumData.albums?.forEach((album) => {
    album?.artists?.forEach((artist) => {
      if (artist.name !== name) {
        collaborators.add(artist.name);
      }
    });
  });

  const numFollowers = followers?.total;
  const formattedFollowers = numFollowers?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const genreList = genres && genres?.length > 0 ? genres?.join(', ') : ['music'];
  const mostPopular = topTracks?.[0]?.name;

  const albumsPerYearCurrentYear = albums.filter((album) => {
    return album.release_date.split('-')[0] === endYear.toString();
  }).length;
  const albumsPerYearLastYear = albums.filter((album) => {
    return album.release_date.split('-')[0] === (endYear - 1).toString();
  }).length;
  const albumsPerYearTwoYearsAgo = albums.filter((album) => {
    return album.release_date.split('-')[0] === (endYear - 2).toString();
  }).length;
  const albumsPerYear =
    (albumsPerYearCurrentYear + albumsPerYearLastYear + albumsPerYearTwoYearsAgo) / 3;

  const totalSongs = allAlbums.reduce((acc, curr) => {
    return acc + curr.total_tracks;
  }, 0);

  const songsPerYearCurrentYear = singles.filter((album) => {
    return album.release_date.split('-')[0] === endYear.toString();
  }).length;
  const songsPerYearLastYear = singles.filter((album) => {
    return album.release_date.split('-')[0] === (endYear - 1).toString();
  }).length;
  const songsPerYearTwoYearsAgo = singles.filter((album) => {
    return album.release_date.split('-')[0] === (endYear - 2).toString();
  }).length;
  const songsPerYear =
    (songsPerYearCurrentYear + songsPerYearLastYear + songsPerYearTwoYearsAgo) / 3;

  const summary = `${name} is a ${genreList} artist with ${formattedFollowers} spotify followers. They have released ${numAlbums} albums and ${totalSongs} songs between ${startYear} and ${endYear}, averaging ${Math.abs(
    Number(albumsPerYear.toFixed(0))
  )} albums and ${Math.abs(Number(songsPerYear.toFixed(0)))} singles per year over their ${Math.abs(yearsActive)}-year career.`;

  console.log('showHalfBio', showHalfBio);
  if (showHalfBio) {
    return <span>{summary}..</span>;
  }

  return (
    <span>
      {isArtistPage ? (
        <>{name}</>
      ) : (
        <Link to={getArtistPath(song?.artistUsername || song?.artist)} component={RouterLink}>
          <Chip
            size="small"
            label={name}
            sx={{ ...chipStyleLink(color || 'var(--color7)'), fontSize: 'inherit' }}
            icon={<InsertLinkTwoTone />}
          />
        </Link>
      )}{' '}
      is a {genreList} artist with {formattedFollowers} spotify followers. They have released{' '}
      {isArtistPage ? (
        <>{numAlbums} albums</>
      ) : (
        <Chip
          size="small"
          label={numAlbums + ' albums'}
          sx={{
            ...(chipStyle as object),
            mr: '7px',
            fontSize: 'inherit',
            color: color ? 'inherit' : 'var(--textColor)',
            backgroundColor:
              tinycolor(generateColor(color)?.bgColor || '#000').getBrightness() > 60
                ? 'rgba(255, 255, 255, 0.07)'
                : 'rgba(1, 1, 1, 0.07)',
            '&:hover': { opacity: 0.9, color: color ? 'inherit' : 'var(--textColor)' },
          }}
        />
      )}{' '}
      and {totalSongs} songs between {startYear} and {endYear}, averaging{' '}
      {Math.abs(Number(albumsPerYear.toFixed(0)))} albums and{' '}
      {Math.abs(Number(songsPerYear.toFixed(0)))} singles per year over their{' '}
      {Math.abs(yearsActive)}-year career. {name} has collaborated with artists like{' '}
      {isArtistPage
        ? [...collaborators].slice(0, 5).join(', ')
        : [...collaborators].slice(0, 5).map((collaborator, index, array) => {
            const isLast = index === array.length - 2;
            return (
              <React.Fragment key={index}>
                <Chip
                  size="small"
                  label={collaborator}
                  sx={{
                    ...(chipStyle as object),
                    mr: isLast ? '7px' : '2px',
                    fontSize: 'inherit',
                    color: color ? 'inherit' : 'var(--textColor)',
                    backgroundColor:
                      tinycolor(generateColor(color)?.bgColor || '#000').getBrightness() > 60
                        ? 'rgba(255, 255, 255, 0.07)'
                        : 'rgba(1, 1, 1, 0.07)',
                    '&:hover': { opacity: 0.9, color: color ? 'inherit' : 'var(--textColor)' },
                  }}
                />
                {isLast && '&'}
              </React.Fragment>
            );
          })}
      . {name}'s most popular song is{' '}
      {isArtistPage ? (
        <Link
          to={getArtistPath(
            topTracks?.[0]?.artists[0]?.name,
            '/songs/' + safeStringWithHyphens(mostPopular)
          )}
          component={RouterLink}
          sx={{
            color: 'inherit',
            borderBottom: '1px solid',
            '&:hover': { color: 'inherit', borderBottom: 'none' },
          }}
        >
          {mostPopular}
        </Link>
      ) : (
        <Link
          to={getArtistPath(
            song?.artistUsername || lc(song?.artist),
            '/songs/' + safeStringWithHyphens(mostPopular)
          )}
          component={RouterLink}
        >
          <Chip
            size="small"
            label={mostPopular}
            sx={{ ...chipStyleLink(color || 'var(--color7)'), fontSize: 'inherit' }}
            icon={<InsertLinkTwoTone />}
          />
        </Link>
      )}
      .
    </span>
  );
};

export const getArtistPath = (artistName?: string, subPath: string = ''): string => {
  const slug = toArtistSlug(artistName);

  if (!slug || isOpenArtistSlug(slug)) {
    return subPath || '/';
  }

  return `/${slug}${subPath}`;
};

export interface OptimizedImageOptions {
  width?: number;
  height?: number;
  fit?: 'contain' | 'cover' | 'fill';
  quality?: number;
}

export const getOptimizedImageUrl = (
  url: string | undefined,
  options: OptimizedImageOptions = {}
): string => {
  if (!url) return '';

  const isExternalOptimizable = url.includes('i.scdn.co') || url.includes('i.ytimg.com');

  if (!isExternalOptimizable) return url;

  const params = new URLSearchParams();
  params.set('url', url);

  if (options.width) params.set('w', options.width.toString());
  if (options.height) params.set('h', options.height.toString());
  if (options.fit) params.set('fit', options.fit);
  if (options.quality) params.set('q', options.quality.toString());

  const result = `/.netlify/images?${params.toString()}`;
  // console.log("[Image CDN]", { original: url, optimized: result });
  return result;
};

export const linkSongsInBio = (
  text: string,
  artistName: string,
  songTitles: string[],
  songMappings?: Record<string, string>
): React.ReactNode => {
  if (!text || !songTitles || songTitles.length === 0) return text;

  const parts: React.ReactNode[] = [];
  let remainingText = text;
  let keyIndex = 0;

  const normalizedSongs = songTitles
    .filter((title) => title && title.length > 0)
    .map((title) => ({
      original: title,
      normalized: title.toLowerCase(),
      // Match both quoted ("title", "title") and unquoted title mentions
      // Priority: exact quoted matches, then case-insensitive word boundaries
      quotedPattern: new RegExp(`[""]${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[""]`, 'gi'),
      wordPattern: new RegExp(`\\b${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi'),
    }));

  while (remainingText.length > 0) {
    let earliestMatch: { index: number; length: number; title: string } | null =
      null;

    // First pass: look for quoted matches
    for (const song of normalizedSongs) {
      song.quotedPattern.lastIndex = 0;
      const match = song.quotedPattern.exec(remainingText);
      if (match && (!earliestMatch || match.index < earliestMatch.index)) {
        earliestMatch = {
          index: match.index,
          length: match[0].length,
          title: song.original,
        };
      }
    }

    // Also check word boundary matches and compare positions (may appear before quoted matches)
    for (const song of normalizedSongs) {
      song.wordPattern.lastIndex = 0;
      const match = song.wordPattern.exec(remainingText);
      if (match && (!earliestMatch || match.index < earliestMatch.index)) {
        if (song.original.length > 3) {
          earliestMatch = {
            index: match.index,
            length: match[0].length,
            title: song.original,
          };
        }
      }
    }

    if (earliestMatch) {
      if (earliestMatch.index > 0) {
        parts.push(remainingText.substring(0, earliestMatch.index));
      }
      // Check if there's a mapping for this mention
      const actualSongTitle = songMappings?.[earliestMatch.title] || earliestMatch.title;
      parts.push(
        <Link
          key={`song-link-${keyIndex++}`}
          to={getArtistPath(artistName, '/songs/' + safeStringWithHyphens(actualSongTitle))}
          component={RouterLink}
          sx={{
            color: 'var(--logoColor2)',
            textDecoration: 'none',
            borderBottom: '1px solid var(--logoColor2)',
            '&:hover': {
              borderBottom: 'none',
            },
          }}
        >
          {remainingText.substring(earliestMatch.index, earliestMatch.index + earliestMatch.length)}
        </Link>
      );
      remainingText = remainingText.substring(earliestMatch.index + earliestMatch.length);
      normalizedSongs.forEach((s) => {
        s.quotedPattern.lastIndex = 0;
        s.wordPattern.lastIndex = 0;
      });
    } else {
      parts.push(remainingText);
      break;
    }
  }

  return <>{parts}</>;
};
